import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Animated, Image, View } from 'react-native';

import FastImage from 'react-native-fast-image';

import { profilePlaceholder } from 'assets/images';

import { PROFILE_ICON } from 'config/testID';

import { isWeb } from 'utils/responsiveness';

import NagraImage from 'components/NagraImage';
import {
    AspectRatio,
    ImageContentType,
    ImageSize,
    ImageType,
} from 'components/NagraImage/types';

import styles from './styles';

type Props = {
    iconId: string;
    width?: number;
    withPlaceholder?: boolean;
    source?: string;
};

function ProfileLogo({
    iconId,
    withPlaceholder,
    width = ImageSize.PROFILE_IMAGE.width,
    source,
}: Props) {
    const [imageSource, setImageSource] = useState<string | undefined>(
        undefined,
    );
    const [imageLoaded, setImageLoaded] = useState(false);

    const fadeAnim = useRef<Animated.Value>(
        new Animated.Value(withPlaceholder ? 0 : 1),
    );

    const handleImageLoad = useCallback(() => {
        if (withPlaceholder) {
            Animated.timing(fadeAnim.current, {
                toValue: 1,
                duration: 150,
                useNativeDriver: !isWeb,
            }).start(() => {
                setImageLoaded(true);
            });
        }
    }, [withPlaceholder]);

    const handleImageError = useCallback(() => {
        setImageSource(profilePlaceholder);

        if (withPlaceholder) {
            Animated.timing(fadeAnim.current, {
                toValue: 0,
                duration: 150,
                useNativeDriver: !isWeb,
            }).start(() => {
                setImageLoaded(false);
            });
        }
    }, [withPlaceholder]);

    useEffect(() => {
        setImageSource(undefined);
        setImageLoaded(false);
    }, [iconId, source]);

    return (
        <View
            style={[styles.root, { width, height: width }]}
            testID={PROFILE_ICON}
        >
            {!imageLoaded && withPlaceholder && (
                <Image
                    source={profilePlaceholder}
                    style={styles.container}
                    resizeMode="cover"
                />
            )}
            <Animated.View
                style={[styles.container, { opacity: fadeAnim.current }]}
            >
                <NagraImage
                    source={imageSource || source}
                    resizeMode={FastImage.resizeMode.contain}
                    cache={
                        isWeb
                            ? FastImage.cacheControl.cacheOnly
                            : FastImage.cacheControl.immutable
                    }
                    style={[styles.root, { width, height: width }]}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    priority={FastImage.priority.high}
                    contentType={ImageContentType.PROFILE}
                    contentId={iconId}
                    imageType={ImageType.AVATAR}
                    width={width}
                    height={width}
                    aspect={AspectRatio.PERSON}
                />
            </Animated.View>
        </View>
    );
}

export default ProfileLogo;
